import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  ClipboardCheck,
  CheckCircle,
  AlertCircle,
  Send,
  Star,
  User,
  Sparkles,
  Users,
} from "lucide-react";
import {
  getPublicProbationReview,
  submitPublicProbationReview,
} from "@/services/probationReviewService";

const RATING_LABELS = {
  1: "Weak",
  2: "Below Average",
  3: "Average",
  4: "Good",
  5: "Strength",
};

const QUESTIONS = [
  {
    id: "q1",
    field: "q1_work_quality_rating",
    commentField: "q1_work_quality_comment",
    title: "1. Quality of work",
    description: "Accuracy, standard and correctness of the employee's output",
  },
  {
    id: "q2",
    field: "q2_quantity_timing_rating",
    commentField: "q2_quantity_timing_comment",
    title: "2. Quantity / Ability to complete tasks on time",
    description: "Volume of work delivered and timeliness",
  },
  {
    id: "q3",
    field: "q3_dependability_rating",
    commentField: "q3_dependability_comment",
    title: "3. Dependability",
    description:
      "Reliability in carrying out assigned duties without constant supervision",
  },
  {
    id: "q4",
    field: "q4_teamwork_rating",
    commentField: "q4_teamwork_comment",
    title: "4. Team work",
    description: "Ability to work effectively with colleagues",
  },
  {
    id: "q5",
    field: "q5_attitude_rating",
    commentField: "q5_attitude_comment",
    title: "5. General attitude to work",
    description: "Disposition, punctuality and eagerness towards work",
  },
  {
    id: "q6",
    field: "q6_personality_rating",
    commentField: "q6_personality_comment",
    title: "6. Personality / Behaviour",
    description: "Interpersonal conduct and professionalism",
  },
  {
    id: "q7",
    field: "q7_integrity_rating",
    commentField: "q7_integrity_comment",
    title: "7. Integrity",
    description: "Do you think the employee can be trusted?",
  },
  {
    id: "q8",
    field: "q8_hygiene_rating",
    commentField: "q8_hygiene_comment",
    title: "8. Hygiene / Dressing",
    description: "Personal cleanliness and appearance / corporate dressing",
  },
  {
    id: "q9",
    field: "q9_fit_rating",
    commentField: "q9_fit_comment",
    title: "9. Fit into the Team",
    description:
      "Do you think the employee has what it takes to fit into the team?",
  },
];

const RECOMMENDATION_OPTIONS = [
  { value: "yes", label: "Yes, I recommend confirmation", color: "green" },
  { value: "no", label: "No, I do not recommend confirmation", color: "red" },
  { value: "not_sure", label: "Not sure / Need more time", color: "amber" },
];

const PublicProbationReview = () => {
  const { publicLink } = useParams();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [closed, setClosed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [reviewer, setReviewer] = useState({
    reviewer_name: "",
    reviewer_email: "",
    reviewer_relationship: "",
  });

  const [ratings, setRatings] = useState({});
  const [comments, setComments] = useState({});
  const [recommendation, setRecommendation] = useState("");

  useEffect(() => {
    const fetchReview = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getPublicProbationReview(publicLink);
        const data = res?.data?.review || res?.review;
        if (!data) throw new Error("Review data missing");
        setReview({ ...data, publicLink });
        // Multiple reviewers can submit via the same link; the form only
        // closes when HR marks the review as completed.
        setClosed(data.status === "completed");
      } catch (err) {
        console.error("Error fetching probation review:", err);
        setError("Review not found or the link is invalid/expired.");
      } finally {
        setLoading(false);
      }
    };
    if (publicLink) fetchReview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicLink]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const payload = {
      ...reviewer,
      ratings: {
        ...ratings,
        q1_work_quality_comment: comments.q1_work_quality_comment || null,
        q2_quantity_timing_comment: comments.q2_quantity_timing_comment || null,
        q3_dependability_comment: comments.q3_dependability_comment || null,
        q4_teamwork_comment: comments.q4_teamwork_comment || null,
        q5_attitude_comment: comments.q5_attitude_comment || null,
        q6_personality_comment: comments.q6_personality_comment || null,
        q7_integrity_comment: comments.q7_integrity_comment || null,
        q8_hygiene_comment: comments.q8_hygiene_comment || null,
        q9_fit_comment: comments.q9_fit_comment || null,
        q10_recommend: recommendation,
        q10_comment: comments.q10_comment || null,
      },
    };

    try {
      const res = await submitPublicProbationReview(publicLink, payload);
      if (res.success) {
        setSubmitted(true);
      } else {
        setError(res.message || "Failed to submit review");
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to submit review. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !review) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Review Not Found
          </h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (closed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Review Closed
          </h1>
          <p className="text-gray-600">
            This review has been closed and is no longer accepting responses.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Review Submitted!
          </h1>
          <p className="text-gray-600">
            Thank you for your feedback. Your review has been recorded. Other
            reviewers can also submit their review using this link.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="bg-blue-100 p-3 rounded-lg">
              <ClipboardCheck className="h-8 w-8 text-blue-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">
                Probation Review Form
              </h1>
              <p className="text-gray-600 text-sm mt-1">
                {review?.review_period || "Probation Review"}
              </p>
              <div className="mt-3 bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-sm text-gray-500">Employee under review</p>
                <p className="text-lg font-semibold text-gray-900">
                  {review?.employee_name}
                </p>
                <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-600">
                  {review?.job_title && <span>{review.job_title}</span>}
                  {review?.department && (
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" /> {review.department}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-sm text-amber-800">
          <p className="flex items-start gap-2">
            <Sparkles className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              Please rate each section on a scale of <strong>1 - 5</strong> (1 being{" "}
              <strong>weak</strong>, 5 being a <strong>strength</strong>) and provide a
              brief comment explaining the reason for each rating.
            </span>
          </p>
          {review?.submission_count > 0 && (
            <p className="flex items-center gap-1 mt-2 text-amber-700 border-t border-amber-200 pt-2">
              <Users className="h-4 w-4" />
              {review.submission_count}{" "}
              {review.submission_count === 1 ? "response has" : "responses have"} already
              been submitted. Your review is recorded separately.
            </p>
          )}
        </div>

        {error && <p className="text-red-600 mb-4">{error}</p>}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Reviewer Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User className="h-5 w-5 text-blue-600" /> Your Information
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Your Name *
                </label>
                <input
                  type="text"
                  required
                  value={reviewer.reviewer_name}
                  onChange={(e) =>
                    setReviewer({ ...reviewer, reviewer_name: e.target.value })
                  }
                  placeholder="e.g. John Doe"
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Your Email *
                </label>
                <input
                  type="email"
                  required
                  value={reviewer.reviewer_email}
                  onChange={(e) =>
                    setReviewer({ ...reviewer, reviewer_email: e.target.value })
                  }
                  placeholder="you@company.com"
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium mb-1">
                Relationship to Employee
              </label>
              <input
                type="text"
                value={reviewer.reviewer_relationship}
                onChange={(e) =>
                  setReviewer({
                    ...reviewer,
                    reviewer_relationship: e.target.value,
                  })
                }
                placeholder="e.g. Supervisor, Manager, Colleague"
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Rating sections */}
          {QUESTIONS.map((q) => (
            <div
              key={q.id}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <h3 className="font-semibold text-gray-900">{q.title}</h3>
              {q.description && (
                <p className="text-sm text-gray-500 mt-1">{q.description}</p>
              )}

              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">Rating (1 - 5)</p>
                <div className="flex gap-2 flex-wrap">
                  {[1, 2, 3, 4, 5].map((value) => {
                    const selected = ratings[q.field] === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() =>
                          setRatings({ ...ratings, [q.field]: value })
                        }
                        className={`flex flex-col items-center justify-center w-14 py-2 rounded-lg border transition-colors ${
                          selected
                            ? "bg-blue-600 border-blue-600 text-white"
                            : "bg-white border-gray-300 text-gray-700 hover:border-blue-400"
                        }`}
                      >
                        <span className="text-lg font-bold">{value}</span>
                        <span
                          className={`text-[10px] ${
                            selected ? "text-blue-100" : "text-gray-400"
                          }`}
                        >
                          {RATING_LABELS[value]}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {ratings[q.field] && (
                  <p className="text-sm text-blue-700 mt-1 flex items-center gap-1">
                    <Star className="h-3.5 w-3.5" />
                    Rating: {ratings[q.field]} - {RATING_LABELS[ratings[q.field]]}
                  </p>
                )}
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium mb-1">
                  Comment / Reason for rating *
                </label>
                <textarea
                  rows={2}
                  required
                  value={comments[q.commentField] || ""}
                  onChange={(e) =>
                    setComments({
                      ...comments,
                      [q.commentField]: e.target.value,
                    })
                  }
                  placeholder="Briefly explain your rating..."
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          ))}

          {/* Q10 Recommendation */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="font-semibold text-gray-900">
              10. Do you recommend {review?.employee_name || "the employee"} for
              confirmation?
            </h3>
            <div className="mt-4 space-y-2">
              {RECOMMENDATION_OPTIONS.map((opt) => {
                const selected = recommendation === opt.value;
                const colorMap = {
                  green: "border-green-500 bg-green-50 text-green-800",
                  red: "border-red-500 bg-red-50 text-red-800",
                  amber: "border-amber-500 bg-amber-50 text-amber-800",
                };
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setRecommendation(opt.value)}
                    className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                      selected
                        ? colorMap[opt.color]
                        : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
              {recommendation === "no" && (
                <p className="text-sm text-red-600 mt-2">
                  Note: If you marked "No", please explain clearly in the comment
                  below.
                </p>
              )}
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium mb-1">
                Comment
              </label>
              <textarea
                rows={2}
                value={comments.q10_comment || ""}
                onChange={(e) =>
                  setComments({ ...comments, q10_comment: e.target.value })
                }
                placeholder="Any additional remarks on this recommendation..."
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-blue-600 text-white py-3 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Send className="h-4 w-4" />
            {submitting ? "Submitting..." : "Submit Review"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PublicProbationReview;